import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hotel } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import GoogleButton from "@/components/GoogleButton";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [role, setRole] = useState(sp.get("role") === "host" ? "host" : "traveler");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const r = sp.get("role");
    if (r === "host" || r === "traveler") setRole(r);
  }, [sp]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register({ name, email, password, role });
      toast.success(`Welcome, ${u.name}!`);
      navigate(u.role === "host" ? "/host" : u.role === "service_provider" ? "/provider" : "/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <div className="bg-white rounded-2xl border border-[#EBEBEB] p-8 shadow-sm">
        <div className="flex items-center justify-center mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#FF385C] flex items-center justify-center">
            <Hotel className="w-6 h-6 text-white" />
          </div>
        </div>
        <h1 className="text-2xl font-display font-bold text-center mb-1">Create your account</h1>
        <p className="text-sm text-[#717171] text-center mb-6">Join the commission-free travel revolution</p>

        <div className="flex bg-[#F7F7F9] rounded-xl p-1 mb-6">
          <button data-testid="role-traveler-btn" type="button" onClick={() => setRole("traveler")} className={`flex-1 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${role === "traveler" ? "bg-white shadow text-[#222]" : "text-[#717171]"}`}>Traveler</button>
          <button data-testid="role-host-btn" type="button" onClick={() => setRole("host")} className={`flex-1 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${role === "host" ? "bg-white shadow text-[#222]" : "text-[#717171]"}`}>Host</button>
          <button data-testid="role-provider-btn" type="button" onClick={() => setRole("service_provider")} className={`flex-1 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${role === "service_provider" ? "bg-white shadow text-[#222]" : "text-[#717171]"}`}>Service provider</button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="name" className="mb-1 block text-sm">Full name</Label>
            <Input id="name" data-testid="register-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="rounded-xl" />
          </div>
          <div>
            <Label htmlFor="email" className="mb-1 block text-sm">Email</Label>
            <Input id="email" data-testid="register-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-xl" />
          </div>
          <div>
            <Label htmlFor="password" className="mb-1 block text-sm">Password</Label>
            <Input id="password" data-testid="register-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="rounded-xl" />
          </div>
          <Button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white h-11">
            {loading ? "Creating..." : `Create ${role} account`}
          </Button>
        </form>

        <div className="mt-4"><GoogleButton role={role === "host" ? "host" : "traveler"} label="Sign up with Google" /></div>

        <div className="text-center text-sm text-[#717171] mt-6">
          Already have an account?{" "}
          <Link data-testid="register-login-link" to="/login" className="text-[#FF385C] font-semibold hover:underline">Log in</Link>
        </div>
      </div>
    </div>
  );
}
